import { useRef, useState } from 'react';
import { rovingRadioTabIndex, useRovingRadioGroup } from '../components/rovingFocus';
import { ModalOverlay } from '../components/ModalOverlay';
import { useModalA11y } from '../components/useModalA11y';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  StarIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { Rating } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { interpolate } from './format';

/**
 * Modale de NOTATION DE MASSE (LOT 3) : un sélecteur d'étoiles 0..5 pour appliquer une
 * note à TOUTE la sélection d'un coup, + Appliquer / Annuler.
 *
 * Vraie boîte de dialogue (`role="dialog"` + `aria-modal`) calquée sur `ConfirmModal` :
 * focus initial, restauration du focus, verrou de défilement, Échap ferme (sauf pendant
 * l'appel réseau), fermeture au clic sur le fond. Le caller l'enveloppe dans un
 * `<Portal>` (l'en-tête porte `backdrop-blur`).
 *
 * SÉLECTEUR D'ÉTOILES : `role=radiogroup` (0 = « non noté »), chaque étoile est un `radio`
 * dont l'état plein/vide est porté par la FORME (`fill`) ET `aria-checked` — jamais la
 * couleur seule. Contrairement à `RatingControl` (contrôle optimiste par asset), c'est un
 * simple PICKER de valeur locale : rien n'est écrit avant « Appliquer ». Re-cliquer
 * l'étoile courante remet à 0 ; un bouton « Retirer la note » l'atteint aussi.
 *
 * Clavier : flèches + `tabIndex` mobile (`rovingFocus.ts`) avec la SÉLECTION QUI SUIT LE
 * FOCUS, motif ARIA standard. Rien ne s'oppose ici à l'activation automatique — à la
 * différence de `RatingControl`, une flèche ne fait que bouger un état local.
 */
interface BulkRatingModalProps {
  /** Nombre d'assets de la sélection (affiché dans l'intro). */
  count: number;
  /** Appelé avec la note choisie (0..5) — l'appel réseau est géré par le caller. */
  onApply: (rating: Rating) => void;
  onClose: () => void;
  /** Appel réseau en cours (désactive les contrôles). */
  busy: boolean;
  /** Message d'erreur (403 lecture seule ou échec générique), déjà traduit. */
  error?: string | undefined;
}

const STARS: readonly Rating[] = [1, 2, 3, 4, 5];

export function BulkRatingModal({
  count,
  onApply,
  onClose,
  busy,
  error,
}: BulkRatingModalProps): React.JSX.Element {
  const { t } = useI18n();
  const [value, setValue] = useState<Rating>(0);
  const onStarsKeyDown = useRovingRadioGroup('auto');
  const applyRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y({ dialogRef, initialFocusRef: applyRef, onEscape: !busy ? onClose : undefined });

  return (
    <ModalOverlay onDismiss={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('bulkRating.title')}
        className="flex max-h-full w-full max-w-md flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
            <StarIcon className="h-6 w-6 text-muted" aria-hidden="true" />
            {t('bulkRating.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            title={t('bulkRating.cancel')}
            aria-label={t('bulkRating.cancel')}
            className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div className="flex min-h-0 flex-col gap-3 overflow-y-auto p-4 text-sm text-fg">
          <p className="text-muted">{interpolate(t('bulkRating.intro'), { count })}</p>

          <div className="flex items-center gap-2">
            <div
              role="radiogroup"
              aria-label={t('rating.label')}
              onKeyDown={onStarsKeyDown}
              className="inline-flex items-center gap-0.5"
            >
              {STARS.map((n) => {
                const filled = n <= value;
                // Re-cliquer l'étoile de la note courante remet à 0.
                const target: Rating = n === value ? 0 : n;
                return (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={n === value}
                    // Note 0 (« non noté ») : aucune étoile cochée → la 1re est le point d'entrée.
                    tabIndex={rovingRadioTabIndex(n === value, n === STARS[0], value > 0)}
                    disabled={busy}
                    onClick={() => setValue(target)}
                    aria-label={interpolate(t('rating.star'), { n })}
                    title={interpolate(t('rating.star'), { n })}
                    className="rounded p-0.5 text-fg hover:text-brand disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                  >
                    <StarIcon
                      className="h-7 w-7"
                      fill={filled ? 'currentColor' : 'none'}
                      aria-hidden="true"
                    />
                  </button>
                );
              })}
            </div>
            {value > 0 && (
              <button
                type="button"
                disabled={busy}
                onClick={() => setValue(0)}
                className="rounded-md px-2 py-1 text-xs text-muted hover:bg-surface-2 hover:text-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              >
                {t('rating.clear')}
              </button>
            )}
          </div>
          {/* État sélectionné en texte (jamais la couleur/forme seule). */}
          <p className="text-xs text-muted" aria-live="polite">
            {value === 0
              ? t('bulkRating.unrated')
              : interpolate(t('bulkRating.selected'), { rating: value })}
          </p>

          {error && (
            <p role="alert" className="flex items-start gap-2 text-danger">
              <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <span>{error}</span>
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border p-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            {t('bulkRating.cancel')}
          </button>
          <button
            ref={applyRef}
            type="button"
            onClick={() => onApply(value)}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy && <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />}
            {busy ? t('bulkRating.applying') : t('bulkRating.apply')}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
